/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "theme-ui";

import React, { useCallback, useEffect, useState } from "react";
import { signIn, signOut, fetchUserAttributes } from "aws-amplify/auth";
import { useTransition } from "react-spring";
import { useTranslation } from "react-i18next";

import useAuth from "../../../hooks/useAuth";
import ModalWithPortal from "../ModalWithPortal";
import HeaderSection from "./HeaderSection";
import LogOutPopUp from "./LogOutPopUp";
import LogInForm from "./Forms/LogInForm";
import NewPasswordChallengeForm from "./Forms/NewPasswordChallengeForm";
import PasswordResetForm from "./Forms/PasswordResetForm";
import Toast from "./Toast";

import useHashLocation from "../../../use-hash-location";
import { Alert } from "@mui/material";

const Authentication = () => {
  const [isOpenPortal, setIsOpenPortal] = useState(false);
  const [openLogoutLink, setOpenLogoutLink] = useState(false);
  const [challengeActive, setChallengeActive] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [userChallenge, setUserChallenge] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser } = useAuth();
  const [location, setLocation] = useHashLocation();
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState(null);

  const handleOpenPortal = () => {
    setIsOpenPortal(true);
  };

  async function handleSignOut() {
    try {
      await signOut();
      // console.log("signing out");
    } catch (error) {
      console.log("error signing out: ", error);
    }
  }

  const exitLocationLogin = () => {
    if (location === "/" || location === "/login") {
      setLocation(`${location.replace("login", "")}`);
    } else {
      setLocation(`${location.replace("/login", "")}`);
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: data.username,
        password: data.password,
      });

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        setChallengeActive(true);
        setUserChallenge({ username: data.username });
        setIsLoading(false);
      } else if (isSignedIn) {
        // exitAfterLogin();
        // exitLocationLogin();
        setIsLoading(false);
      }
    } catch (error) {
      console.log("error signing in", error);
      setErrorMessage(error.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpenPortal) {
      setChallengeActive(false);
      setPasswordReset(false);
    }
  }, [isOpenPortal]);

  useEffect(() => {
    if (passwordChanged) {
      setTimeout(() => {
        setPasswordChanged(false);
      }, 4000);
    }
  }, [passwordChanged]);

  const ToastTransition = useTransition(passwordChanged, {
    from: { x: -100, opacity: 0 },
    enter: { x: 0, opacity: 1 },
    leave: { x: -100, opacity: 0 },
  });

  const setLocationLogin = () => {
    if (location !== "/") {
      setLocation(`${location}/login`);
    } else {
      setLocation(`${location}login`);
    }
  };

  useEffect(() => {
    if (location.includes("login") && currentUser === null) {
      setIsOpenPortal(true);
    } else if (location.includes("login") && currentUser !== null) {
      setLocation("/");
    }
  }, [location]);

  const [userAttributes, setUserAttributes] = useState({ email: "" });

  useEffect(() => {
    if (currentUser) {
      fetchUserAttributes().then((data) => setUserAttributes(data));
    }
  }, [currentUser]);

  return (
    <>
      <div
        style={{
          display: "flex",
          alignContent: "center",
          alignItems: "center",
        }}
      >
        <span
          style={{
            cursor: "pointer",
            color: currentUser !== null ? "#3eba7c" : "#fff",
            pointerEvents: "auto",
            fontSize: "0.8vw",
          }}
          onClick={currentUser !== null ? () => setOpenLogoutLink(true) : () => setLocationLogin() && handleOpenPortal()}
        >
          {currentUser !== null ? userAttributes.email.split("@")[0] : t("BUTTON_LOGIN")}
        </span>
        {currentUser !== null && (
          <svg
            style={{
              paddingLeft: "0.5rem",
            }}
            width="0.9vw"
            height="0.4vw"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 9 7"
          >
            <path
              id="Polygon_34"
              data-name="Polygon 34"
              d="M4.5,0,9,7H0Z"
              transform={`${openLogoutLink ? "translate(9 7) rotate(180)" : ""}`}
              fill="#3eba7c"
            />
          </svg>
        )}
      </div>

      {openLogoutLink && (
        <LogOutPopUp setOpenLogoutLink={setOpenLogoutLink} openLogoutLink={openLogoutLink} signOut={handleSignOut} />
      )}

      <ModalWithPortal
        isOpenPortal={isOpenPortal}
        setLocation={() => {
          exitLocationLogin();
          setIsOpenPortal(false);
        }}
      >
        <div
          sx={{
            background: "#fff",
            width: ["50vw", "50vw", "25vw"],
            padding: "1.5vw",
            paddingBottom: "2vw",
            zIndex: "99999999999999999999999999999999",
          }}
        >
          <div
            style={{
              width: "100%",
            }}
          >
            <HeaderSection
              setLocation={() => {
                exitLocationLogin();
                setIsOpenPortal(false);
              }}
              challengeActive={challengeActive}
              passwordReset={passwordReset}
            />
            {!challengeActive && !passwordReset ? (
              <>
                <LogInForm
                  setIsOpenPortal={setIsOpenPortal}
                  onSubmit={onSubmit}
                  setIsLoading={setIsLoading}
                  isLoading={isLoading}
                  setPasswordReset={setPasswordReset}
                />
                {errorMessage && (
                  <Alert severity="error">
                    <span>{errorMessage}</span>
                  </Alert>
                )}
                {ToastTransition((style, item) => item && <Toast style={style} />)}
              </>
            ) : passwordReset && !challengeActive ? (
              <PasswordResetForm setPasswordReset={setPasswordReset} setPasswordChanged={setPasswordChanged} />
            ) : challengeActive && !passwordReset ? (
              <NewPasswordChallengeForm setChallengeActive={setChallengeActive} user={userChallenge} />
            ) : null}
          </div>
        </div>
      </ModalWithPortal>
    </>
  );
};

export default Authentication;
